import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DataTypeComponent } from '../../data-type-component';
import { PlayersDetailsService } from '../../../../../feature/manage/player/player-details/players-details.service';
import { PromoClaimRateService } from './promo-claim-rate.service';
import {
  PROMO_CLAIM_RATE_INDICATOR_DESCRIPTIONS,
  PromoClaimRateDTO,
  PromoClaimRateIndicator,
  PromoClaimRateRowData,
  PromoClaimRateTrigger,
} from './promo-claim-rate.model';

@Component({
  selector: 'app-promo-claim-rate',
  templateUrl: './promo-claim-rate.component.html',
  styleUrls: ['./promo-claim-rate.component.scss'],
  imports: [DatePipe, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
})
export class PromoClaimRateComponent extends DataTypeComponent {
  readonly playerId = input<number | undefined>(undefined);
  private readonly rowPlayerId = signal<number | undefined>(undefined);
  private readonly effectivePlayerId = computed(() => this.playerId() ?? this.rowPlayerId());
  readonly promoClaimRate = signal<PromoClaimRateDTO | undefined>(undefined);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);

  readonly statusTrigger = computed(() =>
    this.promoClaimRate()?.overridden ? PromoClaimRateTrigger.MANUAL : PromoClaimRateTrigger.AUTOMATED
  );

  readonly lastUpdated = computed(() => {
    const rate = this.promoClaimRate();
    return rate?.overridden ? rate.override?.dateCreated : rate?.calculated?.dateUpdated;
  });

  readonly indicatorTooltip = Object.values(PromoClaimRateIndicator)
    .map((indicator) => `${indicator}: ${PROMO_CLAIM_RATE_INDICATOR_DESCRIPTIONS[indicator]}`)
    .join('\n');

  private readonly destroyRef = inject(DestroyRef);
  private readonly playersDetailsService = inject(PlayersDetailsService);

  constructor(private readonly service: PromoClaimRateService) {
    super();
    effect(() => {
      const playerId = this.effectivePlayerId();
      if (playerId != null) {
        untracked(() => this.load(playerId));
      }
    });
    this.playersDetailsService.refresh$.pipe(takeUntilDestroyed()).subscribe(() => {
      const playerId = this.effectivePlayerId();
      if (playerId != null) {
        this.load(playerId);
      }
    });
  }

  override setRowData(entity: PromoClaimRateRowData) {
    this.rowPlayerId.set(entity?.details?.playerId);
  }

  private load(playerId: number) {
    this.loading.set(true);
    this.loadFailed.set(false);
    this.service
      .get(playerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => {
          this.promoClaimRate.set(d);
          this.loading.set(false);
        },
        error: () => {
          this.loadFailed.set(true);
          this.loading.set(false);
        },
      });
  }
}
